// What the counter has to record for a chainstitch service, and the rules
// that keep the record worth having.
//
// These four lines are the whole catalogue (fxrybackend/scripts/
// add-chainstitch-services.js). Three of them need details before the work
// can be done at all, and the details are also the only way we will ever know
// which designs sell: the service name alone says "a design", never which
// one. Free text in "Notes to our tailors" cannot be counted, and none of
// this can be filled in later, so it is captured as fields at the counter.
//
// Pure: no React. Fixtures in chainstitch.test.mjs.

/** The numbered designs on each printed sheet. Page 1 is the $65 sheet, page
 *  2 the $95 one. Change these when the sheets change.
 *
 *  The counter writes the design's NAME now (Calley, 31 Oct 2026). The
 *  numbers stay because the sheet is still numbered and because the range is
 *  what catches a page 2 design rung in under the page 1 line, which is a $30
 *  mistake. A bare number is read against them; anything else is a name. */
export const DESIGN_NUMBERS = {
  "CHAINSTITCH - SMALL DESIGN": { first: 1, last: 13 },
  "CHAINSTITCH - LARGE DESIGN": { first: 14, last: 18 },
};

/** The line for taking a pocket off or opening a seam to reach the spot. Not
 *  on the printed sheet on purpose: it is an obstacle, not something anyone
 *  wants to buy, and the counter adds it when the placement needs it. */
export const POCKET_OR_SEAM = "CHAINSTITCH - POCKET OR SEAM";

export const isPocketOrSeam = (serviceName) =>
  String(serviceName ?? "")
    .trim()
    .toUpperCase() === POCKET_OR_SEAM;

/**
 * The chainstitch lines the counter picks as the service, which is all of
 * them except pocket-or-seam.
 *
 * That one is an add-on to a design, not a design (Calley, 31 Oct 2026):
 * "they select small, name or large then the add on is do we have to open a
 * seam to do it". It was showing in the service picker alongside the three
 * real lines, and picking it on its own rang up $30 for no stitching, with
 * no design, thread or placement asked for, because `chainstitchFields`
 * correctly says that line asks for nothing.
 *
 * The tick box on the detail screen is the only way it is added, and it is
 * offered there once the garment has something stitched on it.
 */
export const pickableServices = (services) =>
  (services || []).filter((service) => !isPocketOrSeam(service?.name));

/** Where on the garment it goes, in the counter's words. Pinned on the
 *  garment with the customer as well, which is what makes the words a
 *  reminder rather than the whole instruction. */
export const MAX_PLACEMENT = 60;

/**
 * What a typed design answer means: a name, a number off this sheet, or a
 * number off the other one.
 *
 *   { kind: "name", name }  |  { kind: "number", number }  |
 *   { kind: "none" }  |  { kind: "wrong-sheet" }
 */
export const readDesign = (value, range) => {
  const text = String(value ?? "").trim();
  if (!text) return { kind: "none" };
  // Only a bare number is a number. "Goose 2" is a name.
  if (/^\d+$/.test(text)) {
    const number = Number(text);
    return range && number >= range.first && number <= range.last
      ? { kind: "number", number }
      : { kind: "wrong-sheet" };
  }
  return { kind: "name", name: text };
};

/** The six stitched swatches on the last sheet. Numbered, not named, because
 *  Natalie has not named them yet and a number matches how the designs are
 *  picked. */
export const THREAD_COUNT = 6;

/**
 * A cone off the studio wall, as the counter picked it.
 *
 * The picker is fed by the backend from the same rows as the Thread tab on
 * Manage -> Second Mark (fxrybackend/helper/secondMarkThread.js), so the
 * colour on the order is a cone we own rather than a word. Returns the three
 * things worth keeping, or null for anything that is not a cone.
 *
 * The fiber is copied onto the order instead of being looked up later,
 * because it is the part that matters once the garment is in the machine
 * (wool into a sweater, cotton into denim) and the catalogue row it came from
 * can be re-counted, renamed or run out by then.
 */
export const threadPickFrom = (color) => {
  const code = String(color?.code ?? "").trim();
  if (!code) return null;
  const pick = { thread_code: code };
  const name = String(color?.name ?? "").trim();
  const fiber = String(color?.fiber ?? "").trim();
  if (name) pick.thread_name = name;
  if (fiber) pick.thread_fiber = fiber;
  return pick;
};

/**
 * What a typed thread answer means: a swatch off the sheet, a colour picked
 * off the wall, or nothing usable.
 *
 * Standing at the counter, people point at the cones rather than the sheet,
 * and the six swatches are not the whole wall. So the box takes either. A
 * bare number is read as a swatch; anything else is read as a colour and
 * stored as written.
 *
 * Kept as two separate fields rather than one, so the swatches stay
 * countable. How often people go off the sheet is itself worth knowing: if
 * most of them do, the six swatches are the wrong six.
 *
 *   { kind: "swatch", number }  |  { kind: "wall", color }  |
 *   { kind: "none" }  |  { kind: "bad-number" }
 */
export const readThread = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return { kind: "none" };
  // Only a bare number is a swatch. "3 but darker" is a colour instruction
  // and is kept word for word rather than flattened to 3.
  if (/^\d+$/.test(text)) {
    const number = Number(text);
    return number >= 1 && number <= THREAD_COUNT
      ? { kind: "swatch", number }
      : { kind: "bad-number" };
  }
  return { kind: "wall", color: text };
};

/** The cap printed on the sheet. Rebecca is selling "up to 18 letters", so
 *  the box is what holds us to it. */
export const MAX_LETTERS = 18;

/** True for any of the four chainstitch lines. Matched on the name because
 *  this runs on the cart, where a service is a snapshot rather than a
 *  catalogue row, and the name is the part that is always there. */
export const isChainstitchName = (serviceName) =>
  String(serviceName ?? "")
    .trim()
    .toUpperCase()
    .startsWith("CHAINSTITCH - ");

/**
 * Which boxes to show for a service, and the design range to accept.
 *
 * - A name line asks for the letters, capped at 18.
 * - A design line asks which design. A name is what gets written; a bare
 *   number is still read against its own sheet, so typing 15 under SMALL
 *   DESIGN says the wrong line was rung in, a $30 difference worth catching
 *   at the counter.
 * - Taking a pocket off asks for nothing. It is not stitching.
 * - Everything that is stitched asks where on the garment, and for the
 *   thread.
 */
export const chainstitchFields = (serviceName) => {
  const name = String(serviceName ?? "")
    .trim()
    .toUpperCase();
  if (!isChainstitchName(name)) return null;
  const designs = DESIGN_NUMBERS[name] || null;
  const letters = name === "CHAINSTITCH - NAME OR INITIALS";
  const stitched = letters || Boolean(designs);
  return {
    letters,
    designs,
    // The pocket-or-seam line is the only one that puts no thread in the
    // garment, so it is the only one that asks neither of these.
    placement: stitched,
    thread: stitched,
  };
};

// ---- The spot, asked on the service screen ---------------------------------
//
// Where it goes and whether we have to open the garment up are one decision,
// made with the garment in hand (Calley, 31 Oct 2026): a name on a jeans
// pocket means the pocket comes off, and that is +$30. So both are asked on
// the screen where the line and its price are picked, and what gets stitched
// (the design, the letters, the thread) is asked on the screen after it.

/** Where on the garment, or "" when the line is ready. Anything stitched has
 *  to say: a design nobody can place cannot be stitched, and the pin that
 *  goes in beside it is not something the order page can read. */
export const placementError = (serviceName, placement) => {
  const fields = chainstitchFields(serviceName);
  if (!fields?.placement) return "";
  return String(placement ?? "").trim().length === 0
    ? "Where on the garment does it go?"
    : "";
};

/** The spot to store on the service, or null. Merged with what the next
 *  screen records, never replacing it. */
export const placementDetails = (serviceName, placement) => {
  const fields = chainstitchFields(serviceName);
  if (!fields?.placement) return null;
  const spot = String(placement ?? "")
    .trim()
    .slice(0, MAX_PLACEMENT);
  return spot ? { placement: spot } : null;
};

/**
 * What is missing, as a sentence for the counter, or "" when the line is
 * ready.
 *
 * One message at a time and in the order the boxes appear, so fixing it is
 * one correction rather than a list to work through.
 */
export const chainstitchError = (serviceName, details) => {
  const fields = chainstitchFields(serviceName);
  if (!fields) return "";
  const {
    letters = "",
    design = "",
    thread = "",
    threadColor = null,
  } = details || {};

  if (fields.letters && String(letters).trim().length === 0) {
    return "Type the letters to stitch.";
  }
  if (String(letters).trim().length > MAX_LETTERS) {
    return `That is ${String(letters).trim().length} letters. The sheet says up to ${MAX_LETTERS}.`;
  }
  if (fields.designs) {
    const answer = readDesign(design, fields.designs);
    const { first, last } = fields.designs;
    if (answer.kind === "none") {
      return "Which design? Write its name.";
    }
    // A number off the other sheet is the wrong line rung in, not a typo.
    if (answer.kind === "wrong-sheet") {
      return `This sheet is numbered ${first} to ${last}. Check which line was rung in, or write the design's name.`;
    }
  }
  if (fields.thread && !threadPickFrom(threadColor)) {
    const answer = readThread(thread);
    if (answer.kind === "none") {
      return "Which thread? Tap the color they picked, or write it in the box underneath.";
    }
    if (answer.kind === "bad-number") {
      return `The swatch sheet is numbered 1 to ${THREAD_COUNT}. Tap the cone they picked, or write the color by name.`;
    }
  }
  return "";
};

/**
 * The record to store on the service, or null when there is nothing to store.
 *
 * Kept narrow on purpose: only the fields this line actually asked for, so a
 * pocket removal does not carry an empty design number that a count would
 * later have to filter out.
 */
export const chainstitchDetails = (serviceName, details) => {
  const fields = chainstitchFields(serviceName);
  if (!fields) return null;
  const {
    letters = "",
    design = "",
    thread = "",
    threadColor = null,
  } = details || {};
  const record = {};
  if (fields.letters) record.letters = String(letters).trim();
  if (fields.designs) {
    const answer = readDesign(design, fields.designs);
    // Two fields, never both, the same way the thread works. `design` stays
    // a number so the older orders and anything counting them still line up;
    // `design_name` is what the sheet is read by now.
    if (answer.kind === "number") record.design = answer.number;
    if (answer.kind === "name") record.design_name = answer.name;
  }
  if (fields.thread) {
    const pick = threadPickFrom(threadColor);
    if (pick) {
      // One cone, recorded as a cone. This is the path to want: it counts,
      // it says cotton or wool, and it names a colour anybody can find on
      // the wall again in six weeks.
      Object.assign(record, pick);
    } else {
      const answer = readThread(thread);
      // Two fields, never both: `thread` stays countable across orders, and
      // `thread_other` records that the sheet did not have what they wanted.
      if (answer.kind === "swatch") record.thread = answer.number;
      if (answer.kind === "wall") record.thread_other = answer.color;
    }
  }
  return Object.keys(record).length ? record : null;
};
